import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { UserPlus, RefreshCw, Trash2, MailIcon, Users as UsersIcon } from "lucide-react";

type PortalUser = {
  user_id: string;
  email: string;
  role: "admin" | "editor";
  created_at: string;
  last_sign_in_at: string | null;
  invited_at: string | null;
  confirmed_at: string | null;
};

const Users = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<PortalUser[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "editor">("admin");
  const [inviting, setInviting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PortalUser | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-manage-users", {
      body: { action: "list" },
    });
    setLoading(false);
    if (error) {
      toast({ title: "Failed to load users", description: error.message, variant: "destructive" });
      return;
    }
    setUsers(data.users ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const handleInvite = async () => {
    setInviting(true);
    const { data, error } = await supabase.functions.invoke("admin-manage-users", {
      body: { action: "invite", email: inviteEmail.trim(), role: inviteRole },
    });
    setInviting(false);
    if (error || data?.error) {
      toast({
        title: "Invite failed",
        description: error?.message ?? data?.error,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Invitation sent", description: `${inviteEmail} will receive an email shortly.` });
    setInviteOpen(false);
    setInviteEmail("");
    setInviteRole("admin");
    load();
  };

  const handleRoleChange = async (u: PortalUser, newRole: "admin" | "editor") => {
    if (u.role === newRole) return;
    const { data, error } = await supabase.functions.invoke("admin-manage-users", {
      body: { action: "update_role", user_id: u.user_id, role: newRole },
    });
    if (error || data?.error) {
      toast({
        title: "Update failed",
        description: error?.message ?? data?.error,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Role updated" });
    load();
  };

  const handleResend = async (u: PortalUser) => {
    const { data, error } = await supabase.functions.invoke("admin-manage-users", {
      body: { action: "resend_invite", email: u.email },
    });
    if (error || data?.error) {
      toast({
        title: "Resend failed",
        description: error?.message ?? data?.error,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Invitation resent" });
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    const { data, error } = await supabase.functions.invoke("admin-manage-users", {
      body: { action: "remove", user_id: removeTarget.user_id },
    });
    if (error || data?.error) {
      toast({
        title: "Remove failed",
        description: error?.message ?? data?.error,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Access removed" });
    setRemoveTarget(null);
    load();
  };

  return (
    <>
      <Helmet>
        <title>Users · Admin · AI-labbet</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Portal users</h1>
          <p className="text-muted-foreground mt-1">Manage who can access the admin portal.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="sm:w-auto w-full">
          <UserPlus className="w-4 h-4 mr-2" />
          Invite user
        </Button>
      </header>

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Last sign-in</th>
              <th className="text-left px-4 py-3">Added</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t border-border">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-28" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-16 ml-auto" /></td>
                </tr>
              ))}
            {!loading && users?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10">
                  <div className="flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
                    <UsersIcon className="w-8 h-8 opacity-60" />
                    <span>No portal users yet.</span>
                    <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)}>
                      <UserPlus className="w-4 h-4 mr-2" /> Invite a user
                    </Button>
                  </div>
                </td>
              </tr>
            )}
            {users?.map((u) => {
              const isMe = u.user_id === me?.id;
              const pending = !u.confirmed_at;
              return (
                <tr key={u.user_id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.email}</span>
                      {isMe && <Badge variant="secondary" className="text-[10px]">you</Badge>}
                      {pending && <Badge variant="outline" className="text-[10px]">invited</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={u.role}
                      onValueChange={(v) => handleRoleChange(u, v as "admin" | "editor")}
                      disabled={isMe}
                    >
                      <SelectTrigger className="w-32 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {u.last_sign_in_at
                      ? new Date(u.last_sign_in_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handleResend(u)} aria-label="Resend invite">
                            <MailIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Resend invite / password link</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRemoveTarget(u)}
                            disabled={isMe}
                            aria-label="Remove access"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {isMe ? "You cannot remove yourself" : "Remove access"}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="ghost" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a user</DialogTitle>
            <DialogDescription>
              They'll receive an email with a link to set a password and gain access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "admin" | "editor")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access</SelectItem>
                  <SelectItem value="editor">Editor — contacts only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail}>
              {inviting ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.email} will lose access to the admin portal immediately. Their auth account is kept; you can re-invite them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove}>Remove access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Users;
